<template>
  <div class="row flex-center" :class="{ tableTopSelet: showLeftName }">
    <div class="leftname" v-if="showLeftName">{{ leftname }}</div>
    <div class="flex-1">
      <el-popover
        v-model:visible="visible"
        placement="bottom"
        :width="560"
        trigger="click"
        :disabled="disabled"
        popper-class="pc-select-tag-popper"
        @show="onPopoverShow"
      >
        <div class="row">
          <!-- 左侧已选标签预览 -->
          <div class="patientLabel-left">
            <div class="tag-title p-title-line mb10">已选标签</div>
            <ul class="row flex-center flex-wrap">
              <template v-for="(labelItem, index) in selectedTags" :key="`${labelItem.id}-${index}`">
                <li class="list-item canclose font-333">
                  {{ labelItem.labelName }}
                  <el-icon @click.stop="removeTag(labelItem)" class="ml4">
                    <Close />
                  </el-icon>
                </li>
              </template>
            </ul>
          </div>

          <!-- 右侧标签选择列表 -->
          <div class="patient-label-list fs14">
            <div class="column list-tab">
              <el-input
                v-model="searchKey"
                class="mb10"
                placeholder="请输入关键字"
                clearable
              />
              <div class="list-content flex-1">
                <div
                  class="list-group mb10"
                  v-for="(groupItem, groupIndex) in filteredLabelList"
                  :key="`${groupItem.id}-${groupIndex}`"
                  v-show="groupItemShow(groupItem)"
                >
                  <div class="font-666 p-title-line mb10">{{ groupItem.groupName }}</div>
                  <ul class="row flex-center flex-wrap" style="padding-left: 10px;">
                    <template v-for="(labelItem, labelIndex) in groupItem.labelList" :key="`${labelItem.id}-${labelIndex}`">
                      <li
                        v-show="labelItem.status === 0"
                        class="list-item font-333"
                        :class="{ active: isSelected(labelItem) }"
                        @click="selectItem(labelItem, groupItem)"
                      >
                        {{ labelItem.labelName }}
                      </li>
                    </template>
                  </ul>
                </div>
              </div>
            </div>

            <div class="text-center mt20">
              <el-button plain class="mr10" @click="closePopper">取消</el-button>
              <el-button type="primary" @click="handleConfirm">保存</el-button>
            </div>
          </div>
        </div>

        <!-- 触发选择的下拉 -->
        <template #reference>
          <el-select
            v-model="labelValues"
            placeholder="请选择"
            :popper-append-to-body="false"
            multiple
            :collapse-tags="true"
            collapse-tags-tooltip
            class="fw100"
            :disabled="disabled"
            @remove-tag="handleRemoveTag"
          >
            <el-option
              v-for="item in labelOptions"
              :key="item.id"
              :label="item.labelName"
              :value="item.id"
            />
          </el-select>
        </template>
      </el-popover>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { Close } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import PinyinMatch from "pinyin-match";
import { deepClone } from "@/util/util";
import { getPatientLabelByChain } from "@/api/pc/patientinformation.js";

const props = defineProps({
  // v-model 数组，兼容 modelValue 与 value
  modelValue: {
    type: Array,
    default: () => [],
  },
  value: {
    type: Array,
    default: () => [],
  },
  showLeftName: {
    type: Boolean,
    default: true,
  },
  leftname: {
    type: String,
    default: "标签",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // 最大可选择标签数量，0 表示不限制
  maxTag: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["update:modelValue", "update:value", "change"]);

const visible = ref(false);
const searchKey = ref("");
const chainLabelList = ref([]); // 全部标签列表
const selectedTags = ref([]); // 弹窗内已选标签
const labelOptions = ref([]); // el-select 选项展示的标签
const labelValues = ref([]); // el-select 绑定的值（标签 id）

// 统一入口值，优先 modelValue，其次 value，保持向后兼容
const normalizedValue = computed(() =>
  props.modelValue && props.modelValue.length ? props.modelValue : props.value
);

// 构建标签索引，加速根据 id 获取标签详细信息
const labelIndexMap = computed(() => {
  const labelMap = new Map();
  chainLabelList.value.forEach((groupItem) => {
    groupItem.labelList.forEach((labelItem) => {
      labelMap.set(labelItem.id, {
        ...labelItem,
        labelType: groupItem.labelType,
        labelColor: groupItem.labelColor,
        labelFontColor: groupItem.labelFontColor,
      });
    });
  });
  return labelMap;
});

// 根据搜索关键字过滤标签分组
const filteredLabelList = computed(() => {
  const currentSearchKey = searchKey.value;
  const filterArr = [];
  chainLabelList.value.forEach((item, index) => {
    filterArr[index] = deepClone(item);
    filterArr[index].labelList = filterArr[index].labelList.filter((label) =>
      currentSearchKey ? PinyinMatch.match(label.labelName, currentSearchKey) : true
    );
  });
  return filterArr;
});

// 初始化或输入值变化时，同步已选标签
const syncSelectedFromValue = () => {
  const incomingList = normalizedValue.value || [];
  const nextSelected = [];
  incomingList.forEach((item) => {
    if (item && typeof item === "object") {
      nextSelected.push(deepClone(item));
    } else {
      const findLabel = labelIndexMap.value.get(item);
      if (findLabel) {
        nextSelected.push(deepClone(findLabel));
      }
    }
  });
  selectedTags.value = nextSelected;
  labelOptions.value = deepClone(nextSelected);
  labelValues.value = nextSelected.map((item) => item.id);
};

// 监听外部绑定值变化
watch(
  () => normalizedValue.value,
  () => {
    syncSelectedFromValue();
  },
  { deep: true }
);

// 监听弹窗内选择变化，同步下拉展示
watch(
  () => selectedTags.value,
  (val) => {
    labelOptions.value = deepClone(val);
    labelValues.value = val.map((item) => item.id);
  },
  { deep: true }
);

// 获取标签数据，带 Session 缓存
const getPatientLabelList = async () => {
  const cacheKey = "patientLabelChain";
  try {
    const cachedData = sessionStorage.getItem(cacheKey);
    if (cachedData) {
      chainLabelList.value = JSON.parse(cachedData).filter((item) => item.labelList.length);
      syncSelectedFromValue();
      return;
    }
    const {
      data: { data },
    } = await getPatientLabelByChain();
    const filteredData = data.filter((item) => item.labelList.length);
    sessionStorage.setItem(cacheKey, JSON.stringify(filteredData));
    chainLabelList.value = filteredData;
    syncSelectedFromValue();
  } catch (error) {
    // 接口失败时仅日志，不打断交互
    console.error("获取标签列表失败:", error);
  }
};

onMounted(() => {
  getPatientLabelList();
});

const onPopoverShow = () => {
  // 打开弹窗时使用最新的选择
  selectedTags.value = deepClone(labelOptions.value || []);
};

const closePopper = () => {
  visible.value = false;
};

/**
 * 判断标签是否已选中
 */
const isSelected = (labelItem) => selectedTags.value.findIndex((item) => item.id === labelItem.id) !== -1;

/**
 * 点击选择/取消标签
 */
const selectItem = (labelItem, groupItem) => {
  const nextSelected = deepClone(selectedTags.value);
  const findLabelIndex = nextSelected.findIndex((item) => item.id === labelItem.id);
  if (findLabelIndex !== -1) {
    nextSelected.splice(findLabelIndex, 1);
  } else {
    if (props.maxTag > 0 && nextSelected.length >= props.maxTag) {
      // 超过最大限制时给出提示
      ElMessage.warning(`最多只能选择${props.maxTag}个标签`);
      return;
    }
    nextSelected.push({
      ...labelItem,
      labelType: groupItem.labelType,
      labelColor: groupItem.labelColor,
      labelFontColor: groupItem.labelFontColor,
    });
  }
  selectedTags.value = nextSelected;
};

/**
 * 弹窗内移除标签
 */
const removeTag = (tag) => {
  selectedTags.value = selectedTags.value.filter((item) => item.id !== tag.id);
};

/**
 * el-select 移除标签
 */
const handleRemoveTag = (removeLabelId) => {
  removeTag({ id: removeLabelId });
  handleConfirm();
};

/**
 * 保存选择并回填到下拉
 */
const handleConfirm = () => {
  labelOptions.value = deepClone(selectedTags.value);
  labelValues.value = labelOptions.value.map((item) => item.id);
  emit("update:modelValue", deepClone(labelValues.value));
  emit("update:value", deepClone(labelValues.value));
  emit("change", deepClone(labelOptions.value));
  visible.value = false;
};

/**
 * 标签分组是否展示
 */
const groupItemShow = (groupItem) => groupItem.status === 0 && groupItem.labelList.some((item) => item.status === 0);
</script>

<style lang="scss" scoped>
:deep(.el-select) {
  .el-input {
    cursor: pointer;
  }
  .el-select-dropdown {
    display: none;
  }
  .el-select__tags {
    flex-wrap: nowrap;
    overflow: hidden;
    .el-tag {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.tableTopSelet {
  font-size: 14px;
  background-color: #fff;
  &.is-disabled {
    background-color: #f5f7fa;
    .leftname {
      color: #c0c4cc;
      background-color: #fff;
    }
  }
  .leftname {
    text-align: right;
    font-size: 13px;
    line-height: 30px;
    color: #666;
    margin-right: 12px;
    background-color: #fff;
  }

  padding: 0px 0px 0px 12px;
  border-radius: 4px;
  border: 1px solid #e6e6e6;

  :deep(.el-input__inner) {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
    border: none;
    padding-left: 0px;
    line-height: 32px;
    height: 30px;
    color: #333;
  }

  :deep(.el-select) {
    border: none;
  }

  :deep(.el-input) {
    border: none;
  }

  :deep(.el-input.is-focus) {
    border: none;
  }

  :deep(.el-input__inner:hover) {
    border: none;
  }

  :deep(.el-select .el-input.is-focus .el-input__inner) {
    border: none;
  }
}

/* 以下样式沿用标签选择弹窗布局 */
$patientWrapperHeight: 430px;

.mt10 {
  margin-top: 10px;
}

.patientLabel-left {
  width: 240px;
  margin-right: 10px;
  padding-top: 10px;
  height: calc(#{$patientWrapperHeight} - 10px);
  overflow-y: scroll;

  .list-item {
    padding: 0px 10px;
    font-size: 12px;
    line-height: 26px;
    margin-right: 10px;
    border-radius: 5px;
    cursor: pointer;
    margin-bottom: 5px;

    &.canclose {
      background-color: #eaf8f9;
      color: #28b8c3;
      border: 1px solid #eaf8f9;
    }

    .el-icon {
      font-size: 14px;
      vertical-align: middle;
    }
  }
}

.patient-label-list {
  box-sizing: border-box;
  position: relative;
  width: 500px;
  min-height: $patientWrapperHeight;

  .close-icon {
    position: absolute;
    right: 10px;
    top: 10px;
    z-index: 10;
    cursor: pointer;
    font-size: 16px;
  }

  .list-tab {
    height: calc(#{$patientWrapperHeight} - 55px);

    .list-content {
      overflow-y: scroll;

      .list-item {
        padding: 0px 10px;
        font-size: 12px;
        line-height: 26px;
        border-radius: 5px;
        cursor: pointer;
        border: 1px solid #f0f0f0;
        margin-bottom: 6px;
        margin-right: 6px;
        &.active {
          background-color: #eaf8f9;
          color: #28b8c3;
          border: 1px solid #eaf8f9;
        }
      }
    }
  }
}

.p-title-line {
  border-radius: 999px;
}

.mb10 {
  margin-bottom: 10px;
}

.ml4 {
  margin-left: 4px;
}

.mr10 {
  margin-right: 10px;
}

.fs14 {
  font-size: 14px;
}

:deep(.el-select__wrapper) {
    box-shadow: none !important;
    border-left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    box-sizing: border-box;
    height: 34px;
    &.is-focused {
      box-shadow: none !important;
      .el-select__input-wrapper {
        box-shadow: none !important;
      }
    }
  }


</style>